import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { MessageCircleHeart, X, Send, CheckCircle2, LifeBuoy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SupportChat = () => {
  const [open, setOpen] = useState(false);
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [messaggio, setMessaggio] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!nome.trim() || !email.trim() || !messaggio.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/start`, {
        nome: nome.trim(),
        email: email.trim(),
        messaggio: messaggio.trim(),
      });
      setSent(true);
    } catch {
      toast.error("Invio non riuscito, riprova tra poco");
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    setOpen(false);
    if (sent) {
      setSent(false);
      setNome("");
      setEmail("");
      setMessaggio("");
    }
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="support-chat-open"
        aria-label="Apri la chat di assistenza"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, type: "spring", stiffness: 260, damping: 18 }}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 rounded-full bg-[#0A192F] text-white pl-4 pr-5 py-3.5 shadow-[0_16px_40px_rgba(10,25,47,0.35)] hover:bg-[#10B981] hover:shadow-[0_16px_40px_rgba(16,185,129,0.45)] transition-all duration-300 group"
      >
        <MessageCircleHeart className="w-5 h-5 text-[#10B981] group-hover:text-white transition-colors" />
        <span className="text-sm font-bold">Hai domande?</span>
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={close}
              className="fixed inset-0 z-[60] bg-[#0A192F]/40 backdrop-blur-sm sm:hidden"
            />
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 300, damping: 26 }}
              data-testid="support-chat-panel"
              className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-[70] w-full sm:w-[380px] sm:rounded-[1.75rem] rounded-t-[1.75rem] bg-white shadow-[0_30px_80px_rgba(10,25,47,0.35)] border border-slate-200 overflow-hidden flex flex-col max-h-[85vh]"
            >
              {/* Header */}
              <div className="bg-[#0A192F] text-white px-5 py-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#10B981]">
                  <LifeBuoy className="w-4.5 h-4.5 text-white" />
                </span>
                <div className="flex-1 leading-tight">
                  <p className="font-display font-extrabold text-sm">Parla con noi</p>
                  <p className="text-[11px] text-slate-400">Chat libera e gratuita · risponde il titolare</p>
                </div>
                <button type="button" onClick={close} data-testid="support-chat-close" aria-label="Chiudi"
                  className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors">
                  <X className="w-4.5 h-4.5" />
                </button>
              </div>

              {/* Body */}
              {sent ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-12">
                  <span className="w-14 h-14 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-7 h-7 text-[#10B981]" />
                  </span>
                  <p className="font-display font-extrabold text-[#0A192F]" data-testid="support-chat-sent">
                    Messaggio inviato!
                  </p>
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed max-w-[240px]">
                    Ti risponderemo via email il prima possibile. Tieni d'occhio la tua casella.
                  </p>
                  <button type="button" onClick={close}
                    className="mt-6 rounded-full bg-[#0A192F] text-white text-sm font-bold px-6 py-2.5 hover:bg-[#10B981] transition-colors">
                    Chiudi
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-4">
                  <p className="text-[13px] text-slate-500 leading-relaxed">
                    Hai un dubbio su come funziona, sui prezzi o su una richiesta?
                    Scrivici qui: <strong className="text-[#0A192F]">risponde direttamente il titolare via email</strong>, gratis e senza impegno.
                  </p>
                  <input
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    data-testid="support-chat-nome"
                    placeholder="Il tuo nome"
                    required
                    className="w-full h-11 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-[#0A192F] placeholder:text-slate-400 outline-none focus:border-[#10B981] focus:ring-2 focus:ring-emerald-100 transition"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="support-chat-email"
                    placeholder="La tua email"
                    required
                    className="w-full h-11 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-[#0A192F] placeholder:text-slate-400 outline-none focus:border-[#10B981] focus:ring-2 focus:ring-emerald-100 transition"
                  />
                  <textarea
                    value={messaggio}
                    onChange={(e) => setMessaggio(e.target.value)}
                    data-testid="support-chat-messaggio"
                    placeholder="Scrivi la tua domanda..."
                    required
                    rows={4}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-[#0A192F] placeholder:text-slate-400 outline-none focus:border-[#10B981] focus:ring-2 focus:ring-emerald-100 transition resize-none"
                  />
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="support-chat-send"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#10B981] text-white font-extrabold text-sm px-6 py-3.5 hover:bg-[#059669] transition-colors disabled:opacity-60"
                  >
                    {sending ? (
                      <span className="w-4.5 h-4.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Invia la domanda
                      </>
                    )}
                  </button>
                  <p className="text-[10.5px] text-slate-400 text-center leading-relaxed">
                    La tua email serve solo per ricevere la risposta: non viene mostrata a nessuno.
                  </p>
                </form>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
